import { keyframes } from "@emotion/react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState, type FormEvent, type JSX } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";

import { DotGlobeCanvas } from "../components/globe/DotGlobeCanvas.js";
import { useAuth } from "../auth/AuthProvider.js";
import { safeReturnPath } from "../auth/sessionExpiry.js";
import { PAGE_META } from "./public/content.js";
import { usePageMeta } from "./public/usePageMeta.js";

/** The pending second-factor prompt, as returned by the BFF. */
interface TwoFactorState {
  method: "totp" | "email";
  emailHint: string | null;
}

/* The reference's globe entrance: it starts slightly overscaled and eases
 * back to rest while fading up, so the planet reads as settling into the
 * page rather than appearing in it. Values taken from the support portal's
 * `xop-globe-intro` (scale 1.16 -> 1 over 1500ms on the same easing curve).
 * Expressed as an emotion keyframe rather than a globals.css class because
 * this app styles through MUI — globals.css here is a verbatim copy of
 * xop-edge-staff's and adding to it would be drift. */
const globeIntro = keyframes`
  0%   { opacity: 0; transform: scale(1.16); }
  60%  { opacity: 1; }
  100% { opacity: 1; transform: scale(1); }
`;
import { AuthCard, AuthNotice, SubmitButton } from "./login/AuthCard.js";
import { Field } from "./login/Field.js";
import { EyeIcon, EyeOffIcon } from "./login/icons.js";
import { Nameplate } from "./login/Nameplate.js";

/**
 * Sign-in screen.
 *
 * This is the customer support portal's sign-in
 * (`xop-edge-customer/apps/portal-web/src/pages/auth/AuthLayout.tsx` +
 * `Login.tsx`), rebuilt in this app's stack rather than copied: that portal is
 * Tailwind + semantic CSS tokens, this one is MUI + emotion. Same structure,
 * same beats, in the same order:
 *
 *   1. a white top bar (dark-ink mark · divider · slogan in wide caps · the
 *      light/dark toggle at the far right) — now `PublicLayout`'s, shared
 *      with the rest of the public site;
 *   2. a large bold centred nameplate whose words arrive one at a time;
 *   3. a two-column hero — the dot globe on the left, the sign-in card on the
 *      right — that stacks card-first on a narrow screen;
 *   4. the branded contact footer — also `PublicLayout`'s.
 *
 * Exactly three things differ from the reference, and they are the three the
 * product asked for: the nameplate reads "XOP Reseller.", the card's copy says
 * plainly that this is the reseller portal and points customers elsewhere, and
 * the accent is teal instead of XOP blue (see `styles/accent.ts` for why teal,
 * and `components/globe/DotGlobeCanvas.tsx` for the planet it re-colours).
 * Everything else is meant to be indistinguishable.
 *
 * Submitting posts to the BFF's `POST /api/auth/login`, which delegates the
 * credential check to portal-api and then requires crm-api to resolve an active
 * reseller membership before it will mint a session (see
 * `apps/server/src/routes/auth.ts` for the three gates). The card has TWO
 * states because that flow has two legs: credentials, then — for an account
 * with a second factor, which is every account portal-api enrols — a six-digit
 * code. The second leg re-posts the same credentials with `otp` attached
 * rather than holding a half-authenticated ticket, so nothing about a partial
 * sign-in is retained anywhere.
 */

/** Where a customer who landed here by mistake actually wants to be. Printed,
 * not linked: the whole job of the line is to be readable and memorable, and a
 * link from a sign-in form to another sign-in form is a phishing shape. */
const CUSTOMER_PORTAL_HOST = "support.xopnetworks.com";

/** What "Forgot?" says. Reseller accounts are created and reissued from the XOP
 * command center, not self-service here, so the control explains the real
 * recovery path rather than linking to a route that does not exist. */
const RESET_HINT =
  "Password resets aren't self-service yet — your XOP Networks channel manager can reissue your credentials.";

export function LoginPage(): JSX.Element {
  const { login, user, applicant } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  /* Where to go once signed in, from whichever channel carried it.
   *
   * `state.from` is the router's own, set by `RequireAuth` and by
   * `SessionExpiryBridge`; it is preferred because only this app's code can
   * have written it. `?next=` is the fallback for the one case that cannot
   * use router state — a 401 that arrives before the bridge mounts falls back
   * to a full page load, and a full load carries no state.
   *
   * Both go through `safeReturnPath`, which is what keeps a sign-in screen
   * from being an open redirector: `?next=` sits in a URL anyone can put in
   * front of a reseller, and a link that looks like the reseller portal but
   * lands somewhere else is a phishing primitive. */
  const stateFrom =
    location.state && typeof location.state === "object" && "from" in location.state
      ? String((location.state as { from: unknown }).from)
      : null;
  const from = safeReturnPath(stateFrom ?? searchParams.get("next"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showResetHint, setShowResetHint] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Non-null once portal-api has asked for a second factor. Holding the
   * challenge (rather than a boolean) is what lets the prompt name the
   * method — "your authenticator app" reads very differently from "the code
   * we just emailed to d***@acme.com", and getting it wrong sends people
   * hunting for a message that was never sent. */
  const [challenge, setChallenge] = useState<TwoFactorState | null>(null);

  usePageMeta(PAGE_META.login);

  if (user) return <Navigate to={from} replace />;
  // Signed in as an applicant (gate 3b): the wall is the only page for them.
  if (applicant) return <Navigate to="/onboarding" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    /* The second leg re-sends the credentials with the code. portal-api
     * validates all three together, so there is no half-authenticated
     * ticket for this page to hold on to between the two posts. */
    const result = await login({
      email,
      password,
      ...(challenge ? { otp: otp.trim() } : {}),
    });
    setSubmitting(false);

    if (result.kind === "two_factor") {
      setChallenge({ method: result.method, emailHint: result.emailHint });
      setOtp("");
      return;
    }
    if (result.kind === "applicant") {
      // AuthProvider now holds the applicant; the <Navigate> above fires.
      return;
    }
    if (result.kind === "error") {
      setError(result.message);
      // Wipe the code, keep the challenge: a mistyped digit should re-prompt,
      // not throw the user back to the password field.
      if (challenge) setOtp("");
      return;
    }
    // "ok" — AuthProvider now holds the user, and the <Navigate> above
    // redirects on the next render.
  };

  const startOver = () => {
    setChallenge(null);
    setOtp("");
    setError(null);
  };

  return (
    <Box
      component="div"
      sx={{
        position: "relative",
        zIndex: 1,
        flex: 1,
        width: "100%",
        maxWidth: 1380,
        mx: "auto",
        px: { xs: 2.5, sm: 5, lg: 7 },
        pt: { xs: 4, lg: 6 },
        pb: 2,
      }}
    >
      <Nameplate />

      <Box
        sx={{
          display: "grid",
          alignItems: "center",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(12, minmax(0, 1fr))" },
          rowGap: 6,
          columnGap: { lg: 7 },
        }}
      >
        {/* ── The globe. Ordered second on a narrow screen: the form is what
            someone came for, and on a phone the hero would otherwise push it
            below the fold. Same trade the reference makes. */}
        <Box
          sx={{
            gridColumn: { lg: "span 7" },
            display: "flex",
            justifyContent: "center",
            order: { xs: 2, lg: 1 },
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: 540,
              aspectRatio: "1 / 1",
              animation: `${globeIntro} 1500ms cubic-bezier(0.22, 1, 0.36, 1) both`,
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            {/* Accent bloom behind the planet — what makes the globe sit in
                the page rather than on it. */}
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: "-8%",
                borderRadius: "50%",
                filter: "blur(48px)",
                background: (t) =>
                  `radial-gradient(circle, ${alpha(
                    t.palette.mode === "dark" ? t.palette.accent[400] : t.palette.accent[500],
                    0.34,
                  )}, transparent 65%)`,
              }}
            />
            <DotGlobeCanvas />
          </Box>
        </Box>

        {/* ── The sign-in card. */}
        <Box
          sx={{
            gridColumn: { lg: "span 5" },
            display: "flex",
            order: { xs: 1, lg: 2 },
            minWidth: 0,
          }}
        >
          <AuthCard
            title={challenge ? "Two-step verification" : "Reseller sign-in"}
            description={
              challenge
                ? challenge.method === "email"
                  ? `Enter the 6-digit code we just emailed${
                      challenge.emailHint ? ` to ${challenge.emailHint}` : ""
                    }.`
                  : "Enter the 6-digit code from your authenticator app."
                : `This portal is for XOP Networks resellers and channel partners only. XOP Networks customers sign in at ${CUSTOMER_PORTAL_HOST} instead.`
            }
            footer="Trouble signing in? Contact your XOP Networks account manager."
          >
            <Box
              component="form"
              onSubmit={onSubmit}
              noValidate
              sx={{ mt: 3.5, display: "grid", gap: 2 }}
            >
              {error && <AuthNotice>{error}</AuthNotice>}

              {!challenge ? (
                <>
                <Field
                  id="email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={setEmail}
                  required
                  autoFocus
                  disabled={submitting}
                />

                <Box>
                  <Field
                    id="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={setPassword}
                    required
                    disabled={submitting}
                    rightLabel={
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setShowResetHint((v) => !v)}
                        aria-expanded={showResetHint}
                        sx={{
                          p: 0,
                          border: 0,
                          background: "none",
                          font: "inherit",
                          fontSize: 12,
                          cursor: "pointer",
                          color: (t) =>
                            t.palette.mode === "dark" ? t.palette.accent[400] : t.palette.accent[600],
                          "&:hover": { textDecoration: "underline", textUnderlineOffset: 4 },
                        }}
                      >
                        Forgot?
                      </Box>
                    }
                    adornment={
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        tabIndex={-1}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        aria-pressed={showPassword}
                        disabled={submitting}
                        sx={{
                          display: "inline-flex",
                          height: 32,
                          width: 32,
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 1,
                          border: 0,
                          background: "none",
                          cursor: "pointer",
                          color: "text.disabled",
                          "&:hover": { color: "text.primary" },
                        }}
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </Box>
                    }
                  />
                  {showResetHint && (
                    <Typography
                      component="p"
                      sx={{ mt: 1, fontSize: 12, lineHeight: 1.55, color: "text.secondary" }}
                    >
                      {RESET_HINT}
                    </Typography>
                  )}
                </Box>
                </>
              ) : (
                <>
                  {/* `type="text"` with `inputMode="numeric"`, NOT
                      `type="number"`: a number input strips a leading zero,
                      and one-time codes have them. `autoComplete` is the
                      token browsers and iOS use to offer the code straight
                      from the SMS/authenticator. */}
                  <Field
                    id="otp"
                    label="Verification code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={otp}
                    onChange={setOtp}
                    required
                    autoFocus
                    maxLength={6}
                    disabled={submitting}
                  />
                  <Box
                    component="button"
                    type="button"
                    onClick={startOver}
                    disabled={submitting}
                    sx={{
                      justifySelf: "start",
                      p: 0,
                      border: 0,
                      background: "none",
                      font: "inherit",
                      fontSize: 12,
                      cursor: "pointer",
                      color: (t) =>
                        t.palette.mode === "dark" ? t.palette.accent[400] : t.palette.accent[600],
                      "&:hover": { textDecoration: "underline", textUnderlineOffset: 4 },
                    }}
                  >
                    Use a different account
                  </Box>
                </>
              )}

              <SubmitButton disabled={submitting}>
                {submitting ? "Submitting…" : challenge ? "Verify" : "Submit"}
              </SubmitButton>

              {/* The front door to self-registration (spec E §10.1). Only on
                  the credentials leg — mid-2FA is not the moment. */}
              {!challenge && (
                <Typography component="p" sx={{ mt: 0.5, fontSize: 12.5, lineHeight: 1.6, textAlign: "center", color: "text.secondary" }}>
                  Not a partner yet?{" "}
                  <Box
                    component={Link}
                    to="/register"
                    sx={{
                      color: (t) => (t.palette.mode === "dark" ? t.palette.accent[400] : t.palette.accent[600]),
                      fontWeight: 500,
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline", textUnderlineOffset: 3 },
                    }}
                  >
                    Apply to become an XOP Networks reseller.
                  </Box>
                </Typography>
              )}
            </Box>
          </AuthCard>
        </Box>
      </Box>
    </Box>
  );
}
